/**
 * [2026-07-09] Modal d'alerte « reconnectez votre expéditeur email ».
 *
 * Ciblé (V1) sur Emilie MARTIN (sous-traitante EMC RH) : son expéditeur Office 365
 * (mot de passe expiré côté Microsoft) n'est plus fiable. Ce pop-up l'invite, à la
 * connexion, à reconnecter son expéditeur avec la nouvelle méthode (« Envoi simplifié »
 * ou « Microsoft 365 »), pour ne plus dépendre d'un mot de passe qui expire.
 *
 * S'affiche une fois par session (sessionStorage) et uniquement pour les emails de
 * TARGET_EMAILS. Calqué sur OpenPayeCredentialsAlertModal.
 */
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useAuthStore } from '@/store/auth.store';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// V1 : ciblage manuel (juste Emilie). À élargir/piloter par un flag backend si besoin.
const TARGET_EMAILS = ['emartin@emc-rhconsulting.com'];

const DISMISS_KEY = 'sender-reconnect-alert-dismissed';

export function SenderReconnectAlertModal() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const email = user?.email?.toLowerCase();
    if (!email) return;
    if (!TARGET_EMAILS.includes(email)) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    setIsOpen(true);
  }, [user?.email]);

  const handleClose = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setIsOpen(false);
  };

  const handleGoToSenders = () => {
    handleClose();
    navigate('/senders');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reconnectez votre expéditeur email"
      size="md"
    >
      <div className="space-y-4">
        <div className="flex justify-center">
          <div className="bg-primary-100 flex h-16 w-16 items-center justify-center rounded-full">
            <Mail className="text-primary-600 h-10 w-10" />
          </div>
        </div>

        <div className="space-y-2 text-center">
          <p className="text-sm leading-relaxed text-neutral-700">
            Nous avons fiabilisé l'envoi de vos emails (DSN, bulletins, factures).
            L'ancienne connexion par mot de passe n'est plus assez fiable : un mot de
            passe peut expirer côté Microsoft sans prévenir.
          </p>
          <p className="text-sm leading-relaxed text-neutral-700">
            Pour un envoi durable au nom de votre cabinet, reconnectez votre expéditeur
            avec la nouvelle méthode : «&nbsp;Envoi simplifié&nbsp;» (sans mot de passe)
            ou «&nbsp;Microsoft&nbsp;365&nbsp;» (connexion sécurisée).
          </p>
        </div>

        <div className="flex justify-center gap-3 pt-2">
          <Button variant="outline" onClick={handleClose}>
            Plus tard
          </Button>
          <Button onClick={handleGoToSenders}>Configurer mon expéditeur</Button>
        </div>
      </div>
    </Modal>
  );
}
